import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import Modal from '../components/ui/Modal';
import { Page, PageHeader, Section } from '../components/layout/primitives';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { departmentService } from '../services/departmentService';
import { usersService } from '../services/usersService';
import { AppTable } from '../utils/AppTable';
import MultiSelect from '../utils/MultiSelect';
import { TextField } from '../components/ui/Field';

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  departments: string[];
};

const UsersList: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
  } = useForm<FormValues>();

  const loadDepartments = async () => {
    try {
      const res: any = await departmentService.list();
      const data = res?.departments || [];
      setDepartments(data.map((d: any) => ({ id: String(d.id), name: d.name })));
    } catch {
      toast.error('Failed to load departments');
    }
  };

  useEffect(() => {
    setPage(1);
    setUsers([]);
    setHasMore(true);
  }, [sortBy, sortOrder, pageSize]);

  const loadUsers = useCallback(async () => {
    setLoading(true);

    try {
      const res = await usersService.list({
        page,
        limit: pageSize,
        search: searchTerm,
      });

      const list = res.data?.users || res.users || [];
      const totalCount = res.total || res.data?.total || 0;
      setTotal(totalCount);

      if (viewMode === 'list') {
        setUsers(list);
        setHasMore(false);
        return;
      }

      if (page === 1) {
        setUsers(list);
      } else {
        setUsers((prev) => [...prev, ...list]);
      }

      setHasMore(list.length === pageSize);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, viewMode]);

  const bottomRef = useInfiniteScroll(() => {
    if (viewMode === 'grid' && hasMore && !loading) {
      setPage((p) => p + 1);
    }
  }, loading);

  useEffect(() => {
    setPage(1);
    setUsers([]);
    setHasMore(true);
  }, [searchTerm]);

  useEffect(() => {
    loadUsers();
  }, [page, pageSize, searchTerm, sortBy, sortOrder, loadUsers]);

  useEffect(() => {
    loadDepartments();
  }, []);

  const openCreate = () => {
    setEditUser(null);
    setShowPasswordInput(true);
    reset({ firstName: '', lastName: '', email: '', password: '', departments: [] });
    setIsCreateOpen(true);
  };

  const openEdit = (user: any) => {
    setEditUser(user);
    setShowPasswordInput(false);

    reset({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      password: undefined,
      departments: user.departmentMemberships.map((d: any) => String(d.departmentId)),
    });

    setIsCreateOpen(true);
  };

  const confirmDelete = (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    try {
      await usersService.remove(deleteId!);
      setUsers((p) => p.filter((u) => u.id !== deleteId));
      toast.success('User deleted');
    } catch {
      toast.error('Delete failed');
    } finally {
      setConfirmOpen(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    if (!values.departments.length) {
      toast.error('Please select at least one department');
      return;
    }

    setSaving(true);

    try {
      if (editUser) {
        await usersService.update(editUser.id, values);
        toast.success('User updated');
      } else {
        await usersService.create(values);
        toast.success('User created');
      }

      loadUsers();
      setPage(1);
      setHasMore(true);
      setIsCreateOpen(false);
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const tableColumns = [
    {
      key: 'fullName',
      label: 'Name',
      sortable: true,
      render: (u: any) => `${u.firstName} ${u.lastName}`,
    },
    {
      key: 'departments',
      label: 'Departments',
      render: (u: any) =>
        u.departmentMemberships.map((d: any) => (
          <span key={d.id} className="mr-1 rounded bg-blue-100 px-2 py-1 text-xs text-blue-700">
            {d.department.name}
          </span>
        )),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (u: any) => (
        <div className="flex gap-2">
          <Button onClick={() => openEdit(u)} size="sm" variant="secondary">
            Edit
          </Button>
          <Button onClick={() => confirmDelete(u.id)} size="sm" variant="danger">
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Page contentClassName="py-6">
      <PageHeader
        className="py-0"
        title="Users"
        subtitle="Manage workspace users, departments, and access."
        aside={<Button onClick={openCreate}>+ New User</Button>}
      />

      <Section className="py-6">
        <Card className="mb-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="md:max-w-lg"
            />

            <div className="flex rounded-xl bg-agentic-gray-100 p-1">
              <Button tone="custom"
                onClick={() => setViewMode('grid')}
                className={`rounded-lg px-4 py-2 text-sm ${viewMode === 'grid' ? 'bg-white shadow text-agentic-text-primary' : 'text-agentic-text-secondary'}`}
              >
                Grid
              </Button>
              <Button tone="custom"
                onClick={() => setViewMode('list')}
                className={`rounded-lg px-4 py-2 text-sm ${viewMode === 'list' ? 'bg-white shadow text-agentic-text-primary' : 'text-agentic-text-secondary'}`}
              >
                List
              </Button>
            </div>
          </div>
        </Card>

        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {users.map((u) => (
              <Card key={u.id}>
                <h3 className="font-bold text-agentic-text-primary">{u.firstName} {u.lastName}</h3>
                <div className="mt-2 flex flex-wrap gap-1">
                  {u.departmentMemberships.map((d: any) => (
                    <span key={d.id} className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-700">
                      {d.department.name}
                    </span>
                  ))}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => openEdit(u)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => confirmDelete(u.id)}>
                    Delete
                  </Button>
                </div>
              </Card>
            ))}

            <div ref={bottomRef} className="h-10" />
            {loading ? <p className="col-span-full text-center text-agentic-text-secondary">Loading...</p> : null}
          </div>
        ) : (
          <Card className="overflow-x-auto p-0">
            <AppTable
              columns={tableColumns as any}
              data={users}
              loading={loading}
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              onSort={(key, dir) => {
                setSortBy(key);
                setSortOrder(dir);
              }}
              sortBy={sortBy}
              sortOrder={sortOrder}
            />
          </Card>
        )}
      </Section>

      {isCreateOpen ? (
        <Modal
          isOpen={isCreateOpen}
          onClose={() => !saving && setIsCreateOpen(false)}
          title={editUser ? 'Edit User' : 'Create User'}
          size="md"
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <TextField tone="agentic" {...register('firstName', { required: true })} placeholder="First Name" className="w-full rounded-lg border px-3 py-2" />
            <TextField tone="agentic" {...register('lastName', { required: true })} placeholder="Last Name" className="w-full rounded-lg border px-3 py-2" />
            <TextField tone="agentic" {...register('email', { required: true })} placeholder="Email" className="w-full rounded-lg border px-3 py-2" />

            <MultiSelect
              label="Departments"
              options={departments}
              value={watch('departments')}
              onChange={(val) => setValue('departments', val)}
            />

            {editUser && !showPasswordInput ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowPasswordInput(true)}>
                Change Password
              </Button>
            ) : null}

            {showPasswordInput ? (
              <TextField tone="agentic"
                {...register('password', { minLength: 6 })}
                placeholder="New Password"
                className="w-full rounded-lg border px-3 py-2"
              />
            ) : null}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" type="button" onClick={() => setIsCreateOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" loading={saving} disabled={saving}>
                Save
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doDelete}
        title="Delete User?"
        message="This action cannot be undone."
        variant="error"
      />
    </Page>
  );
};

export default UsersList;
